import React, { useState } from "react";
import { Search, SlidersHorizontal, Check, X, ChevronDown } from "lucide-react";
import DateRangePicker from "./DateRangePicker";

export default function Toolbar({
  searchQuery,
  onSearchChange,
  activeFilter,
  onFilterChange,
  activeSort,
  onSortChange,
  resultCount,
  showHistoryFilters = false,
  dateRange = { start: "", end: "" },
  onDateRangeChange,
  priceRange,
  onPriceRangeChange
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const formatDateStr = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Today's Orders Filter Options
  const todayFilters = [
    { value: "all", label: "All Categories" },
    { value: "Lunch", label: "Lunch Only" },
    { value: "Dinner", label: "Dinner Only" },
    { value: "Delivered", label: "Delivered Status" },
    { value: "Pending", label: "Pending Status" },
    { value: "Cancelled", label: "Cancelled Status" }
  ];

  // History Filter Options (Simplified category options)
  const historyCategories = [
    { value: "all", label: "All Categories" },
    { value: "Lunch", label: "Lunch" },
    { value: "Dinner", label: "Dinner" }
  ];

  // Today's Orders Sort Options
  const sortOptions = [
    { value: "newest", label: "Newest Order" },
    { value: "oldest", label: "Oldest Order" },
    { value: "quantity", label: "Quantity" },
    { value: "price", label: "Price (High-Low)" }
  ];

  return (
    <div className="space-y-4 mb-6">
      {/* Upper Row: Search & Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">

        {/* Search Input (Takes full width on mobile) */}
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const sanitized = e.target.value
                .replace(/^\s+/, "")
                .replace(/<[^>]*>?/gm, "")
                .replace(/[<>]/g, "");
              onSearchChange(sanitized);
            }}
            placeholder={showHistoryFilters ? "Search orders..." : "Search meals..."}
            className="w-full pl-10 pr-4 py-2 bg-bg-primary border border-border-primary rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-0.5 focus:ring-brand-red focus:border-brand-red transition-all"
          />
        </div>

        {/* Filters and Sort Dropdowns */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">

          {/* Result Count (hidden on narrow screens, visible on tablet+) */}
          <span className="hidden md:inline text-xs text-text-secondary mr-2 font-medium bg-bg-secondary px-2.5 py-1.5 rounded-lg border border-border-primary">
            {resultCount} {resultCount === 1 ? "meal" : "meals"}
          </span>

          {/* Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setFilterOpen(!filterOpen); setSortOpen(false); }}
              className={`flex items-center space-x-1.5 px-3 py-2 bg-bg-primary border rounded-lg text-xs font-semibold hover:bg-bg-secondary active:scale-[0.98] transition-all cursor-pointer ${activeFilter !== "all" ? "border-brand-red text-brand-red bg-brand-red-light/10" : "border-border-primary text-text-secondary"
                }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>
                {activeFilter === "all"
                  ? "Filters"
                  : (showHistoryFilters ? historyCategories : todayFilters).find(f => f.value === activeFilter)?.label || "Filtered"}
              </span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {filterOpen && (
              <div className="absolute right-0 mt-1.5 w-52 bg-white border border-border-primary rounded-lg shadow-lg z-30 py-1">
                <span className="block px-3 py-1.5 text-[10px] uppercase font-bold text-text-muted border-b border-border-primary mb-1">
                  Filter By
                </span>
                {(showHistoryFilters ? historyCategories : todayFilters).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onFilterChange(opt.value);
                      setFilterOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left text-xs text-text-secondary hover:bg-bg-secondary hover:text-text-primary transition-colors"
                  >
                    <span>{opt.label}</span>
                    {activeFilter === opt.value && <Check className="w-3.5 h-3.5 text-brand-red" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Picker Filter (Only for Order History) */}
          {showHistoryFilters && (
            <div className="flex items-center gap-2">
              <div className="w-full max-w-sm">
                <DateRangePicker
                  value={[
                    dateRange.start ? new Date(dateRange.start + "T00:00:00") : null,
                    dateRange.end ? new Date(dateRange.end + "T00:00:00") : null
                  ]}
                  onChange={(start, end) => {
                    const startStr = start ? formatDateStr(start) : "";
                    const endStr = end ? formatDateStr(end) : "";
                    onDateRangeChange({ start: startStr, end: endStr });
                  }}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Extra Row: Mobile Result Count */}
      <div className="flex md:hidden justify-between items-center text-xs text-text-secondary px-1">
        <span>Result count: <strong className="text-text-primary">{resultCount}</strong> meals</span>
      </div>
    </div>
  );
}
